// src/strategy/KingMoveStrategy.ts
import { MoveStrategy } from './MoveStrategy';
import { Position, BoardState } from '../model/BoardState';

export class KingMoveStrategy implements MoveStrategy {
  getLegalMoves(position: Position, boardState: BoardState): Position[] {
    const moves: Position[] = [];
    const { row, col } = position;

    // Find the current piece at the given position
    const currentPiece = boardState.pieces.find(p => p.position.row === row && p.position.col === col);
    if (!currentPiece) return moves;

    // Determine the turn based on the color of the current piece
    const turn = currentPiece.color === 'white' ? 'Computer' : 'Player';

    // Possible moves for the king (one square in any direction)
    const possibleMoves = [
      { row: row + 1, col },
      { row: row - 1, col },
      { row, col: col + 1 },
      { row, col: col - 1 },
      { row: row + 1, col: col + 1 },
      { row: row + 1, col: col - 1 },
      { row: row - 1, col: col + 1 },
      { row: row - 1, col: col - 1 },
    ];

    // Find the opponent's king position
    const opponentKing = boardState.pieces.find(p => p.type === 'king' && p.color !== currentPiece.color);
    const opponentKingPosition = opponentKing ? opponentKing.position : null;

    // Iterate over each possible move
    for (const move of possibleMoves) {
      // Check if the move is within the bounds of the board and not blocked by a piece of the same color
      if (this.isWithinBounds(move) && !this.isBlocked(move, boardState, position)) {
        // Check if the move is not adjacent to the opponent's king
        if (!this.isAdjacentToOpponentKing(move, opponentKingPosition)) {
          // Simulate the move to check if it puts the king in check
          const simulatedBoardState = this.simulateMove(position, move, boardState);
          if (!this.isInCheckAfterMove(turn, simulatedBoardState, move)) {
            // If the move does not put the king in check, add it to the list of legal moves
            moves.push(move);
          }
        }
      }
    }

    return moves;
  }

  getStrategyName(): string {
    return "King Move Strategy";
  }

  /**
   * Checks if the given position is adjacent to the opponent's king.
   *
   * @param position - The position to check.
   * @param opponentKingPosition - The position of the opponent's king.
   * @returns `true` if the position is adjacent to the opponent's king, `false` otherwise.
   */
  private isAdjacentToOpponentKing(position: Position, opponentKingPosition: Position | null): boolean {
    if (!opponentKingPosition) return false;

    const rowDiff = Math.abs(position.row - opponentKingPosition.row);
    const colDiff = Math.abs(position.col - opponentKingPosition.col);

    return rowDiff <= 1 && colDiff <= 1;
  }

  /**
   * Checks if the given position is within the bounds of the board.
   *
   * @param position - The position to check.
   * @returns `true` if the position is within bounds, `false` otherwise.
   */
  private isWithinBounds(position: Position): boolean {
    return position.row >= 0 && position.row < 8 && position.col >= 0 && position.col < 8;
  }

  /**
   * Checks if the given position is blocked by a piece of the same color,
   * excluding the opponent's king.
   *
   * @param position - The position to check.
   * @param boardState - The current state of the board.
   * @param currentPosition - The current position of the piece.
   * @returns `true` if the position is blocked, `false` otherwise.
   */
  private isBlocked(position: Position, boardState: BoardState, currentPosition: Position): boolean {
    const piece = boardState.pieces.find(p => p.position.row === position.row && p.position.col === position.col);
    const currentPiece = boardState.pieces.find(p => p.position.row === currentPosition.row && p.position.col === currentPosition.col);

    // Allow moves to positions occupied by the opponent's king
    if (piece && piece.type === 'king' && piece.color !== currentPiece?.color) {
      return false;
    }

    return piece !== undefined && piece.color === currentPiece?.color;
  }

  /**
   * Simulates a move from one position to another on the board.
   *
   * @param from - The starting position of the move.
   * @param to - The ending position of the move.
   * @param boardState - The current state of the board.
   * @returns The new state of the board after the move.
   */
  private simulateMove(from: Position, to: Position, boardState: BoardState): BoardState {
    const simulatedBoardState = new BoardState(
      boardState.pieces.map(piece => ({
        ...piece,
        position: piece.position.row === from.row && piece.position.col === from.col ? to : piece.position,
        moveStrategy: Object.create(Object.getPrototypeOf(piece.moveStrategy), Object.getOwnPropertyDescriptors(piece.moveStrategy))
      }))
    );

    // Remove any captured piece at the destination position
    simulatedBoardState.pieces = simulatedBoardState.pieces.filter(piece => !(piece.position.row === to.row && piece.position.col === to.col && piece.position !== to));

    return simulatedBoardState;
  }

  /**
   * Checks if the king is in check after a move.
   *
   * @param turn - The current turn, either 'Player' or 'Computer'.
   * @param boardState - The current state of the board.
   * @param kingPosition - The position of the king to check.
   * @returns `true` if the king is in check, `false` otherwise.
   */
  private isInCheckAfterMove(turn: 'Player' | 'Computer', boardState: BoardState, kingPosition: Position): boolean {
    const playerColor = turn === 'Player' ? 'black' : 'white';
    const opponentColor = playerColor === 'white' ? 'black' : 'white';

    // Iterate over each opponent piece to check if any can move to the king's position
    for (const piece of boardState.pieces) {
      if (piece.color === opponentColor && piece.type !== 'king') {
        const legalMoves = piece.moveStrategy.getLegalMoves(piece.position, boardState);
        if (legalMoves.some(move => move.row === kingPosition.row && move.col === kingPosition.col)) {
          return true; // The king is in check
        }
      }
    }

    return false; // The king is not in check
  }
}
